/**
 * The hours a record accounts for (M3, Plan D15).
 *
 * For an individual this is just the duration, exactly as M1 showed it. For a
 * group it is not — and showing the duration alone would be the specific lie
 * this whole track exists to prevent.
 *
 * Twelve people for two hours is **24 volunteer-hours**. Rendered as "2h" it
 * reads as an afternoon's help from one person, which is what §4.29's reporting
 * would then quietly disagree with. So the multiplication is shown, not implied:
 *
 *     2h × 12 = 24h
 *
 * `volunteer_minutes` is generated in PostgreSQL, so this displays the stored
 * figure rather than recomputing it — a screen that did its own arithmetic could
 * drift from the number reporting counts.
 */

import { formatDuration } from '@parinaam-vms/shared';
import type { AttendanceDetail } from '@parinaam-vms/shared';
import styles from './VolunteerHours.module.css';

export interface VolunteerHoursProps {
  record: Pick<AttendanceDetail, 'duration_minutes' | 'participant_count' | 'volunteer_minutes'>;
}

export function VolunteerHours({ record }: VolunteerHoursProps) {
  const each = formatDuration(record.duration_minutes);

  if (!record.participant_count || record.participant_count <= 1) {
    return <>{each}</>;
  }

  return (
    <span className={styles.group}>
      <span className={styles.total}>{formatDuration(record.volunteer_minutes)}</span>
      <span className={styles.working}>
        {each} × {record.participant_count}
      </span>
    </span>
  );
}
